import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormGroup,
  Button as MuButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from "@mui/material";
import { parseBrandName } from "../../utils/tableFunctions";
import {
  postMismatchChargeback,
  postMismatchVoid,
  postRefundTransaction,
  postCancelTransaction,
  postVoidTransaction,
  postCaptureTransaction,
} from "../../services/reconciliations";
import { ArrowBack } from "@mui/icons-material";
import { Box } from "@mui/system";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  canBeChargeBacked,
  canBeRefunded,
  canBeCanceled,
  canBeVoided,
  canBeAuthorized,
  canBeCaptured,
  canBePlanSelected,
} from "../../utils/void_refund_logic";
import { linkTextStyle } from "../../utils/styles";
import { handleOpenLink } from "../../utils/appUtils";

const BUTTON_TYPE = {
  VOID: 1,
  CAPTURE: 4
}

const ReconDetailsHeader = ({
  loaded,
  title,
  details,
  userRole,
  itemId
}) => {
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const { id = itemId } = useParams();
  const [selectedButton, setSelectedButton] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [isRefundOpen, setRefundOpen] = useState(false);
  const [isPartial, setIsPartial] = useState(false);
  const [partialAmount, setPartialAmount] = useState(0);

  const handleButtonClick = () => {
    window.close();
  };

  const transButtons = [
    { name: "Void", type: 1, enable: details?.is_allow_void, show: true },
    {
      name: "Chargeback",
      type: 2,
      enable: details?.is_allow_charge_back,
      show: false,
    },
    { name: "Refund", type: 3, enable: details?.is_allow_refund, show: true },
    { name: "Capture", type: 4, enable: details?.is_allow_capture, show: true },
    {
      name: "Authorize",
      type: 5,
      enable: canBeAuthorized(details),
      show: false,
    },
    {
      name: "Plan Selected",
      type: 6,
      enable: details?.is_allow_plan_select,
      show: false,
    },
    { name: "Cancel", type: 7, enable: details?.is_allow_cancel, show: false },
  ].sort((a, b) => (a.name > b.name ? 1 : -1));

  const handleDialogOpen = (buttonType) => {
    // Button
    // type 1: void,
    // type 2: charge back,
    // type 3: refund
    // type 4: capture
    // type 5: authorize
    // type 6: plan selected
    // type 7: cancel

    setSelectedButton(buttonType);

    switch (buttonType) {
      case 1:
      case 2:
      case 4:
      case 5:
      case 6:
      case 7:
        setIsOpen(!isOpen);
        break;
      case 3:
        setRefundOpen(!isRefundOpen);
        break;
      default:
        break;
    }
  };

  const handleDialogClose = () => {
    setIsOpen(false);
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleSubmit = () => {
    if (comment === "") {
      alert("Comment is required!");
    } else {
      switch (selectedButton) {
        case 1:
          postMismatchVoid(id, comment, details.oxygen7_id);
          break;
        case 2:
          postMismatchChargeback(id, comment, details.oxygen7_id);
          break;

        case 7:
          postCancelTransaction(id, comment);
          break;
        default:
          break;
      }
    }
  };

  const renderDialog = () => {
    if (selectedButton === BUTTON_TYPE.VOID) {
      return (
        <Dialog open={isOpen} onClose={handleDialogClose} fullWidth>
        <Stack
          direction="row"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pr: 2,
            alignItems: "center",
            bgcolor: "primary.main",
          }}
        >
          <DialogTitle sx={{ color: "#fff" }}>Confirm Void</DialogTitle>
          <Box>
            <Button
              variant="contained"
              color="error"
              onClick={handleDialogClose}
            >
              Cancel
            </Button>
          </Box>
        </Stack>
        <DialogContent>
          <Box
            noValidate
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              m: "auto",
              alignItems: "center",
            }}
          >
            <FormGroup style={{ width: "70%" }}>
              <Typography variant="body1" sx={{ mt: "25px" }}>
                Choose your void type
              </Typography>
              <Select
                label="Void Type"
                style={{ minWidth: "100%" }}
                value={isPartial ? 2 : 1}
              >
                <MenuItem value={1} onClick={handlePartialClose}>
                  Full
                </MenuItem>
                <MenuItem value={2} onClick={handlePartialOpen}>
                  Partial
                </MenuItem>
              </Select>

              {isPartial ? (
                <>
                  <Typography variant="body1" sx={{ mt: "15px" }}>
                    Partial Void Amount *
                  </Typography>
                  <TextField
                    required
                    id="standard-basic"
                    variant="standard"
                    onChange={handlePartialChange}
                    sx={{
                      mb: "10px",
                      minWidth: "100%",
                    }}
                    type="number"
                  />
                </>
              ) : null}

              <Typography variant="body1" sx={{ mt: "15px" }}>
                Comments *
              </Typography>
              <TextField
                required
                id="standard-basic"
                variant="standard"
                onChange={handleCommentChange}
                sx={{ mb: "10px", minWidth: "100%" }}
              />
            </FormGroup>

            <Button
              variant="contained"
              sx={{ display: "flex", ml: "auto", mt: 3 }}
              onClick={() => handle_Void_NomuPayHost(selectedButton)}
            >
              Submit
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      )
    }

    if (selectedButton === BUTTON_TYPE.CAPTURE) {
      return (
        <Dialog open={isOpen} onClose={handleDialogClose} fullWidth>
        <Stack
          direction="row"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pr: 2,
            alignItems: "center",
            bgcolor: "primary.main",
          }}
        >
          <DialogTitle sx={{ color: "#fff" }}>Confirm Capture</DialogTitle>
          <Box>
            <Button
              variant="contained"
              color="error"
              onClick={handleDialogClose}
            >
              Cancel
            </Button>
          </Box>
        </Stack>
        <DialogContent>
          <Box
            noValidate
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              m: "auto",
              alignItems: "center",
            }}
          >
            <FormGroup style={{ width: "70%" }}>
                <Typography variant="body1" sx={{ mt: "15px" }}>
                  Capture Amount *
                </Typography>
                <TextField
                  required
                  id="standard-basic"
                  variant="standard"
                  onChange={handlePartialChange}
                  sx={{
                    mb: "10px",
                    minWidth: "100%",
                  }}
                  type="number"
                />

              <Typography variant="body1" sx={{ mt: "15px" }}>
                Comments *
              </Typography>
              <TextField
                required
                id="standard-basic"
                variant="standard"
                onChange={handleCommentChange}
                sx={{ mb: "10px", minWidth: "100%" }}
              />
            </FormGroup>

            <Button
              variant="contained"
              sx={{ display: "flex", ml: "auto", mt: 3 }}
              onClick={() => handleCaptureSubmit(selectedButton)}
            >
              Submit
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      )
    }

    return (
      <Dialog open={isOpen} onClose={handleDialogClose} fullWidth>
        <Stack
          direction="row"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pr: 2,
            alignItems: "center",
            bgcolor: "primary.main",
          }}
        >
          <DialogTitle sx={{ color: "#fff" }}>Confirm</DialogTitle>
          <Box>
            <Button
              variant="contained"
              color="error"
              onClick={handleDialogClose}
            >
              Cancel
            </Button>
          </Box>
        </Stack>
        <DialogContent>
          <Box
            noValidate
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              m: "auto",
              alignItems: "center",
            }}
          >
            <FormGroup style={{ width: "70%" }}>
              <Typography variant="body1" sx={{ mt: "15px" }}>
                Comments *
              </Typography>
              <TextField
                required
                id="standard-basic"
                variant="standard"
                onChange={handleCommentChange}
                sx={{ mb: "10px", minWidth: "100%" }}
              />
            </FormGroup>

            <Button
              variant="contained"
              sx={{ display: "flex", ml: "auto", mt: 3 }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  const handlePartialOpen = () => {
    setIsPartial(true);
  };

  const handlePartialClose = () => {
    setIsPartial(false);
  };

  const handleRefundDialogClose = () => {
    setRefundOpen(false);
  };

  const handlePartialChange = (event) => {
    setPartialAmount(parseFloat(event.target.value));
  };

  const handleRefundSubmit = () => {
    if (comment === "") {
      alert("Comment is required!");
    } else {
      if (isPartial) {
        if (isNaN(partialAmount) || partialAmount === "") {
          alert("Amount is required!");
        } else if (partialAmount > details.transaction_amount) {
          alert("Refund amount must be smaller than the net amount!");
        } else {
          postRefundTransaction(id, partialAmount, comment);

          setRefundOpen(false);
          setIsPartial(false);
          setPartialAmount("");
          setComment("");
        }
      } else {
        postRefundTransaction(id, details.transaction_amount, comment);

        setRefundOpen(false);
        setIsPartial(false);
        setPartialAmount("");
        setComment("");
      }
    }
  };

  const handleCaptureSubmit = () => {
    if (comment === "") {
      alert("Comment is required!");
    } else {
        if (isNaN(partialAmount) || partialAmount === "" || partialAmount === 0) {
          alert("Amount is required!");
        } else if (partialAmount > details.transaction_amount) {
          alert("Capture amount must be smaller than the net amount!");
        } else {
          postCaptureTransaction({ pk: id, capture_amount: partialAmount, comment});

          setIsOpen(false);
          setIsPartial(false);
          setPartialAmount("");
          setComment("");
        }
    }
  }

  const handle_Void_NomuPayHost = () => {
    if (comment === "") {
      alert("Comment is required!");
    } else {
      const payload = { pk: id, comment }

      if (isPartial) {
        if (isNaN(partialAmount) || !partialAmount) {
          alert("Amount is required!");
        } else if (partialAmount > details.transaction_amount) {
          alert("Void amount must be smaller than the net amount!");
        } else {
          postVoidTransaction({ ...payload, void_amount: partialAmount });

          setIsOpen(false);
          setIsPartial(false);
          setPartialAmount("");
          setComment("");
        }
      } else {
        postVoidTransaction(payload);

        setIsOpen(false);
        setIsPartial(false);
        setPartialAmount("");
        setComment("");
      }
    }
  };

  const renderRefundDialog = () => {
    return (
      <Dialog open={isRefundOpen} onClose={handleRefundDialogClose} fullWidth>
        <Stack
          direction="row"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pr: 2,
            alignItems: "center",
            bgcolor: "primary.main",
          }}
        >
          <DialogTitle sx={{ color: "#fff" }}>Confirm Refund</DialogTitle>
          <Box>
            <Button
              variant="contained"
              color="error"
              onClick={handleRefundDialogClose}
            >
              Cancel
            </Button>
          </Box>
        </Stack>
        <DialogContent>
          <Box
            noValidate
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              m: "auto",
              alignItems: "center",
            }}
          >
            <FormGroup style={{ width: "70%" }}>
              <Typography variant="body1" sx={{ mt: "25px" }}>
                Choose your refund type
              </Typography>
              <Select
                label="Refund Type"
                style={{ minWidth: "100%" }}
                value={isPartial ? 2 : 1}
              >
                <MenuItem value={1} onClick={handlePartialClose}>
                  Full
                </MenuItem>
                {details?.is_allow_partial_refund && (
                  <MenuItem value={2} onClick={handlePartialOpen}>
                    Partial
                  </MenuItem>
                )}
              </Select>

              {isPartial ? (
                <>
                  <Typography variant="body1" sx={{ mt: "15px" }}>
                    Partial Refund Amount *
                  </Typography>
                  <TextField
                    required
                    id="standard-basic"
                    variant="standard"
                    onChange={handlePartialChange}
                    sx={{
                      mb: "10px",
                      minWidth: "100%",
                    }}
                    type="number"
                  />
                </>
              ) : null}

              <Typography variant="body1" sx={{ mt: "15px" }}>
                Comments *
              </Typography>
              <TextField
                required
                id="standard-basic"
                variant="standard"
                onChange={handleCommentChange}
                sx={{ mb: "10px", minWidth: "100%" }}
              />
            </FormGroup>

            <Button
              variant="contained"
              sx={{ display: "flex", ml: "auto", mt: 3 }}
              onClick={handleRefundSubmit}
            >
              Submit
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      {!itemId && (
          <Stack
            direction="row"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant={mobile ? "h5" : "h3"}>{title}</Typography>
            <MuButton
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={handleButtonClick}
              sx={{ width: "100px" }}
            >
              Back
            </MuButton>
          </Stack>
        )
      }

      {loaded && (
        <>
          <Stack
            direction={mobile ? "column" : "row"}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack>
              <Typography variant={mobile ? "subtitle2" : "h6"}>
                Merchant Name:{" "}
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ ...linkTextStyle }}
                  onClick={() => handleOpenLink(`/3/app/merchant-details/${details.merchant.id}`)}
                >
                  {details.merchant ? details.merchant.name : "-"}
                </Typography>
              </Typography>
              <Typography variant={mobile ? "subtitle2" : "h6"} sx={{ mb: 2 }}>
                Brand: {parseBrandName(details.payment_brand.name)}{" "}
              </Typography>
            </Stack>

            {
              userRole?.includes("Void/Refund/Chargeback") && (
                <Box spacing={7}>
                  {transButtons.map((button) => {
                    return (
                      button.show &&
                      button.enable && (
                        <MuButton
                          variant="outlined"
                          onClick={() => handleDialogOpen(button.type)}
                          sx={{ width: "100px", ml: 2 }}
                          color="error"
                        >
                          {button.name}
                        </MuButton>
                      )
                    );
                  })}
                </Box>
              )
            }

            {mobile && <Box sx={{ height: 10 }} />}
          </Stack>
          {renderDialog()}
          {renderRefundDialog()}
        </>
      )}
    </>
  );
};

export default ReconDetailsHeader;
